import { HttpParams } from '@angular/common/http';
import { SegmentationType, TrainingMethod, WorkflowType } from '@ca/core/models';
import { StepType } from '../../app/core/enums/step-type.enum';
import { SystemModuleType } from '../../app/core/enums/system-module-type.enum';
import { ModuleV1Read, VersionReadModel } from '../../app/core/models/module-v1-read.model';
import { ModuleVersion } from '../../app/core/models/module-version.model';
import { OptionItem } from '../../app/core/models/option-item.model';
import { isInputTypeStatic, ModuleParameter, StepParameter } from '../../app/core/models/parameter.model';
import { Step } from '../../app/core/models/step.model';
import { WorkflowCategoryV1Read } from '../../app/core/models/workflow-category-v1-read';
import { WorkflowNode } from '../../app/core/models/workflow-specification';
import { WorkflowV1Run } from '../../app/core/models/workflow-v1-read.model';
import { Workflow } from '../../app/core/models/workflow.model';
import { SortOption } from '../../app/core/types/sort-option.type';
import { isOldSingleFileUpload, isOldWorkflowStarter } from '../utils';
import { convertKeyValuePairToStepParameter } from './domain-utils';

export function workflowCategoriesToOptionItems(data: WorkflowCategoryV1Read[]): OptionItem<string>[] {
    return data.map((c: WorkflowCategoryV1Read) => ({ label: `${c.name}`, value: c.id }));
}

export function getWorkflowListParams(
    isPrivate: boolean,
    sort: SortOption,
    searchTerm = '',
    pageSize = 10,
    pageNumber = 1,
    categoryIds?: string,
    isAdmin?: boolean,
    isFeatured?: boolean,
    isCommunityContent?: boolean,
): HttpParams {
    let params = new HttpParams()
        .set('page[number]', pageNumber.toString())
        .set('page[size]', pageSize.toString())
        .set('sort', `-isfeatured,${sort}`)
        .set('categoryIds', categoryIds ? categoryIds : '')
        .set('search', searchTerm);

    if (isPrivate) {
        params = params.set('includePublic', 'false').set('includeOwn', 'true');
    }

    if (!isAdmin) {
        params = params.set('type', WorkflowType.UNDEFINED);
    }

    if (isFeatured) {
        params = params.set('isfeatured', isFeatured.toString());
    }

    if (isCommunityContent) {
        params = params.set('isfeatured', 'false');
    }

    return params;
}

export function getDefaultMLWorkflow(workflows: WorkflowV1Run[], trainingMethod: TrainingMethod): WorkflowV1Run | undefined {
    const workflow = workflows.find(
        (wf: WorkflowV1Run) =>
            wf.isDefaultForType &&
            (wf.segmentationTypeSupported == SegmentationType.INSTANCEANDSEMANTIC ||
                (trainingMethod == 'semantic' && wf.segmentationTypeSupported == SegmentationType.SEMANTIC) ||
                (trainingMethod == 'instance' && wf.segmentationTypeSupported == SegmentationType.INSTANCE)),
    );
    if (!workflow && workflows.length > 0) {
        return workflows[0];
    }
    return workflow;
}

export function convertToWorkflow(workflowModel: WorkflowV1Run): Workflow {
    const workflow = new Workflow(workflowModel);

    const flow: WorkflowNode[] = workflowModel!.specification!.workflow!.flow;

    flow.forEach((workflowNode: WorkflowNode, index: number) => {
        const moduleVersionModelId: string = workflowNode.run.module;
        const flowId: string = workflowNode.id;

        const moduleVersion: VersionReadModel | undefined = workflowModel.usedModuleVersions.find(m => m.id === moduleVersionModelId);
        const module: ModuleV1Read | undefined = workflowModel.usedModules.find((m: ModuleV1Read) => m.id === moduleVersion!.moduleId);

        if (module != null) {
            const moduleModel: ModuleV1Read = new ModuleV1Read(module);

            const stepType: StepType = determineModuleStepType(moduleModel, moduleVersion!, index);

            const moduleVersionObj = new ModuleVersion(moduleVersion!, moduleModel);

            const stepToAppend: Step = new Step(flowId, moduleVersionObj, stepType);

            if (workflowNode.run.input != null) {
                Object.entries(workflowNode.run.input).forEach(keyValuePair => {
                    const key = keyValuePair[0];
                    const value = keyValuePair[1];

                    // 260321: Safety check: Some old modules have a wrong structure. A connectable input parameters (non-static) must either
                    // be undefined (if not connected), or be an object with operation_id and output (StepOutputParameter - which defines a connection).
                    // Some old specifications though have an entry in the workflowNode.run.input object which has some value (i.e. empty array).
                    // Here we filter only for valid step parameters.
                    // However, we need to exclude SystemModules here, since they are StepOutputParameters which can hold values (file or file_list).
                    // For Old Single File Upload isInputTypeStatic() will return true because it's a StringStepParameter
                    const moduleVersionParam: ModuleParameter | undefined = moduleVersionObj.getInputParameter(key);
                    if (
                        moduleVersionParam != null &&
                        (isInputTypeStatic(moduleVersionParam.type) ||
                            (value != null && value.operation_id != null) ||
                            stepToAppend.type === StepType.WorkflowDefineInput ||
                            stepToAppend.type === StepType.ModuleDefineInput)
                    ) {
                        const param: StepParameter | undefined = convertKeyValuePairToStepParameter(keyValuePair, stepToAppend, workflow);

                        if (param != null) {
                            stepToAppend.setInputParameter(param);
                        }
                    }
                });
            }

            workflow.appendStep(stepToAppend);
        } else {
            // Put a warning, because the user should be able to recover.
            console.warn('Module with ID ' + (moduleVersion && moduleVersion.moduleId) + ' not found. Maybe it was deleted?');
        }
    });

    return workflow;
}

function determineModuleStepType(module: ModuleV1Read, moduleVersion: VersionReadModel, index: number): StepType {
    let stepType = StepType.Module;
    if (module != null && moduleVersion != null) {
        if (module.systemModuleType === SystemModuleType.DefineWorkflowInput) {
            stepType = StepType.WorkflowDefineInput;
        } else if (module.systemModuleType === SystemModuleType.DefineModuleInput) {
            stepType = StepType.ModuleDefineInput;
        } else if (isOldWorkflowStarter(module.title, index)) {
            stepType = StepType.OldWorkflowStarter;
        } else if (isOldSingleFileUpload(module.title, index)) {
            stepType = StepType.OldSingleFileUpload;
        }
    } else {
        throw new Error('TODO: What to do when a module is not found?');
    }

    return stepType;
}
